import {Grid, Typography, TextField, DialogActions, Button, useTheme, useMediaQuery} from '@mui/material';
import {useState} from 'react';
import {AddTestimonialDTO} from '../../../../api/models';
import {useUpdateNewTestimonial} from '../../../../api/testimonials';
import {generateId} from '../../../../common/utils';
import { FeedbackSeemsInvalidError, UsernameSeemsInvalidError } from '../../../../api/error';

export const AddNewTestimonial = ({
    throwSnackbarMessage,
    refetch,
}: {
    refetch: VoidFunction;
    throwSnackbarMessage: (message: string) => void;
}) => {
    const [userNameText, updateUsernameText] = useState('');
    const [feedbackText, updateFeedbackText] = useState('');
    const theme = useTheme();

    const {addTestimonial} = useUpdateNewTestimonial();

    const resetState = () => {
        updateUsernameText('');
        updateFeedbackText('');
    };

    const validateUsername = (usernameString: string | undefined) => {
        if (usernameString) {
            if(usernameString.length <= 2) {
                throw new UsernameSeemsInvalidError(
                    'Username should have 3 or more characters'
                );
            }
        } else {
            throw new UsernameSeemsInvalidError('Username should not be empty');
        }
    };

    const validateFeedback = (feedbackString: string | undefined) => {
        if (feedbackString) {
            if(feedbackString.length <= 11) {
                throw new FeedbackSeemsInvalidError(
                    'Please write the feedback in depth'
                );
            }
        } else {
            throw new FeedbackSeemsInvalidError('Feedback should not be empty');
        }
    };

    const validateInfo = (): boolean => {
        try {
            validateUsername(userNameText);
            validateFeedback(feedbackText);
            return true;
        } catch (error) {
            if(error instanceof UsernameSeemsInvalidError || error instanceof FeedbackSeemsInvalidError) {
                throwSnackbarMessage(error.message);
            } else {
                console.log(error);
            }
            return false;
        }
    };

    const addNewUserTestimonial = () => {
        const isValid = validateInfo();  
        if(isValid) {
            const addRequestDTO = {
                displayName: userNameText,
                feedbackText: feedbackText,
                action: 'ADD',
            } as AddTestimonialDTO;
            addTestimonial({
                data: {
                    updateTestimonialDTO: addRequestDTO,
                    cb: () => {
                        resetState();
                        refetch();
                    },
                },
                throwSnackbarMessage: throwSnackbarMessage,
            });
        } else {
            console.log('ignore this flow for time being');
        }
    };

    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const buttonStyles = isMobileScreen
        ? {
              padding: '10px 20px',
              fontSize: '14px',
              borderRadius: '8px',
              width: '100%',
              backgroundColor: '#90909d',
              color: 'white',
              overflow: 'hidden',
              textAlign: 'center',
              whitespace: 'nowrap',
              lineHeight: '1.2',
          }
        : {};

    return (
        <div>
            <Grid container>
                <Grid item md={12} xs={12} sm={12}>
                    <Typography
                        sx={{
                            marginTop: 1,
                            fontWeight: 700,
                            color: 'black',
                            fontFamily: 'Poppins',
                        }}
                        variant='body1'>
                        Add New User Testimonial
                    </Typography>
                </Grid>
            </Grid>
            <br />
            <Grid style={{marginTop: 12}} container>
                <Grid item md={12} xs={12} sm={12}>
                    <TextField
                        style={{
                            color: 'black',
                        }}
                        id={generateId('node', 8)}
                        label='User Name'
                        variant='outlined'
                        fullWidth
                        value={userNameText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateUsernameText(e.target.value);
                        }}
                    />
                </Grid>
            </Grid>
            <br />
            <Grid style={{marginTop: 12}} container>
                <Grid item md={12} xs={12} sm={12}>
                    <TextField
                        style={{
                            color: 'black',
                        }}
                        id={generateId('node', 8)}
                        label='Feedback Text'
                        variant='outlined'
                        fullWidth
                        value={feedbackText}
                        multiline
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            updateFeedbackText(e.target.value);
                        }}
                    />
                </Grid>
            </Grid>
            <Grid style={{marginTop: 12}} justifyContent={'center'} container>
                <Grid item>
                    <DialogActions>
                        <Button sx={buttonStyles} onClick={addNewUserTestimonial}>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    color: isMobileScreen ? 'white' : '',
                                    fontFamily: 'Poppins',
                                    whiteSpace: 'nowrap',
                                }}
                                variant='body1'>
                                Save Changes
                            </Typography>
                        </Button>
                        <Button sx={buttonStyles} onClick={resetState}>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    color: isMobileScreen ? 'white' : '',
                                    fontFamily: 'Poppins',
                                    whiteSpace: 'nowrap',
                                }}
                                variant='body1'>
                                Dismiss
                            </Typography>
                        </Button>
                    </DialogActions>
                </Grid>
            </Grid>
        </div>
    );
};
